import { BlogCard } from '../components/BlogCard'
import { InnerSidebar } from '../components/InnerSidebar'
import { useOptionalT } from '../i18n/LocaleProvider'
import { blogRowCards, blogSidebar } from './posts'
import type { BlogPost } from './types'

export interface BlogIndexProps {
  /** The posts this app carries — see `blogContent({ tags })` in `tools/vite-blog.ts`. */
  posts: BlogPost[]
  /** The page's `<h1>`. Each app names its own blog; the default is the generic one. */
  title?: string
  /** Shown instead of the list when this app has no posts yet. */
  emptyLabel?: string
}

/**
 * The blog index — every post as a compact row, beside the shared sidebar.
 *
 * **Shared by all four public apps as of 2026-09-21.** It was `app_website`'s page; the
 * Foundation asked for a blog on every site, and four copies of an index is four indexes
 * that drift. Each app mounts this with the posts its build gave it and nothing else.
 *
 * **No banner.** `Breadcrumb` came off the whole platform, and this page lost its own on
 * 2026-09-19. Two things travel with that and both are the consuming app's to remember:
 *
 *  - `.ul-plain-page-top` on the first section, which is here — the header is absolutely
 *    positioned, so without it the bar sits on top of the first heading, and it also draws
 *    the wash that gives the floating bar something to be legible against.
 *  - **`/blog` in the app's `header.plainRoutes`**, which this component cannot do for
 *    itself. Forget it and every link in the nav is white on near-white.
 *
 * **The `<h1>`.** `Breadcrumb`'s title was this page's only one, and `audit:seo` fails a
 * page with any number other than one.
 *
 * **There is no pager**, and the note where it used to be says why.
 *
 * **An empty blog says so.** A venture nobody has written about yet gets no posts from the
 * tag filter, and "there are no posts about the Garage yet" is a different fact from a page
 * that failed to load. The sidebar is still rendered: its categories and tags are derived
 * from the same empty list, so it renders empty widgets rather than lying about what exists.
 */
export function BlogIndex({ posts, title, emptyLabel }: BlogIndexProps) {
  const t = useOptionalT()
  const cards = blogRowCards(posts)

  return (
    <div className="ul-section-spacing ul-plain-page-top">
      <div className="ul-container">
        <h1 className="ul-plain-page-title">{title ?? t('ul.blog.title')}</h1>

        <div className="row ul-bs-row gy-5 flex-column-reverse flex-md-row">
          {/* The sidebar, on the left from md up. */}
          <div className="col-lg-3 col-md-4">
            <InnerSidebar {...blogSidebar(posts)} />
          </div>

          {/* The posts. */}
          <div className="col-lg-9 col-md-8">
            {cards.length === 0 ? (
              <div className="ul-directory-empty">{emptyLabel ?? t('ul.blog.empty')}</div>
            ) : (
              /*
                **No pager.** `Pagination` is unwired — its links point at `'#'` and it
                always prints three pages — and it was on this page while one app had the
                whole blog and a dozen posts, where it read as furniture. On a venture's
                blog with one post it reads as a claim: three pages of writing, two of them
                one click away and neither of them there. The index shows every post this
                site carries, which is the honest thing until paging is real.
              */
              <div>
                {cards.map((post, i) => (
                  <BlogCard {...post} key={`${post.image}-${i}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
